import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Clock, Eye, ThumbsUp, MessageCircle, Share2, ArrowLeft, Send, BookOpen, Tag, FolderOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO'; // Import the hook
import DynamicBreadcrumb from '../components/DynamicBreadcrumb';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function BlogDetail() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [schemaData, setSchemaData] = useState(null);

  // Use the SEO hook for basic meta tags
  useSEO();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('token')}` })
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          setBlog(data.blog);
          
          // Update page-specific SEO
          updateBlogSEO(data.blog);
          
          // Generate schema.org data
          generateSchemaData(data.blog);
        } else {
          console.error('Failed to fetch blog');
        }
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id, user]);

  const updateBlogSEO = (blogData) => {
    // Override the default SEO with blog-specific data
    const title = `${blogData.title} - NEVXOAULIO VENTURES Blog`;
    const description = blogData.metaDescription || blogData.excerpt || blogData.content?.substring(0, 160) || '';
    const keywords = blogData.tags ? blogData.tags.join(', ') : 'technology blog, software development, AI ML';
    const image = blogData.coverImage || "https://www.nevxoaulio.com/logo/og-image.png";

    // Update meta tags
    document.title = title;
    
    // Helper function to update meta tags
    const updateMetaTag = (name, content, attribute = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (attribute === 'property') {
          meta.setAttribute('property', name);
        } else {
          meta.name = name;
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (blog.publishedAt) {
      updateMetaTag('article:published_time', blog.publishedAt, 'property');
    }
    if (blog.tags) {
      blog.tags.forEach(tag => {
        updateMetaTag('article:tag', tag, 'property');
      });
    }

    updateMetaTag('article:section', blog.category || 'Technology', 'property');
    updateMetaTag('article:author', blog.authorName || 'Nevxoaulio Ventures', 'property');
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('og:title', title, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:type', 'article', 'property');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
  };

  const generateSchemaData = (blogData) => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": blogData.title,
      "description": blogData.metaDescription || blogData.excerpt || blogData.content?.substring(0, 160) || '',
      "image": blogData.coverImage || "https://www.nevxoaulio.com/logo/og-image.png",
      "datePublished": blogData.publishedAt || blogData.createdAt,
      "dateModified": blogData.updatedAt || blogData.publishedAt || blogData.createdAt,
      "author": {
        "@type": "Person",
        "name": blogData.authorName || "Nevxoaulio Ventures",
        "url": "https://nevxoaulio.com/about"
      },
      "publisher": {
        "@type": "Organization",
        "name": "NEVXOAULIO VENTURES",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.nevxoaulio.com/logo/company-logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      },
      "articleSection": blogData.category || "Technology",
      "keywords": blogData.tags ? blogData.tags.join(', ') : "software development, technology, AI, ML",
      "wordCount": blogData.content ? blogData.content.split(/\s+/).length : 0,
      "timeRequired": `PT${blogData.readTime ? blogData.readTime.replace(' min', 'M').replace(' ', '') : '5M'}`,
      "url": window.location.href,
      "commentCount": blogData.comments ? blogData.comments.length : 0,
      "interactionStatistic": [
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/LikeAction",
          "userInteractionCount": blogData.likes ? blogData.likes.length : 0
        },
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/CommentAction",
          "userInteractionCount": blogData.comments ? blogData.comments.length : 0
        },
        {
          "@type": "InteractionCounter",
          "interactionType": "https://schema.org/ViewAction",
          "userInteractionCount": blogData.views || 0
        }
      ]
    };
    
    setSchemaData(schema);
  };

  const handleShareBlog = () => {
    const blogUrl = window.location.href;
    navigator.clipboard.writeText(blogUrl);
    setCopiedLink(true);
    
    // Track share event
    if (window.gtag) {
      window.gtag('event', 'blog_share', {
        blog_id: id,
        blog_title: blog?.title,
        method: 'copy_link'
      });
    }
    
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleLike = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/blog/${id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (!res.ok) {
        console.error('Failed to like blog');
        return;
      }
      
      const data = await res.json();

      // Update blog with new likes array from server
      setBlog(prev => ({
        ...prev,
        likes: data.likes || []
      }));

      // Update schema data
      if (schemaData) {
        setSchemaData(prev => ({
          ...prev,
          interactionStatistic: prev.interactionStatistic.map(stat => 
            stat.interactionType === "https://schema.org/LikeAction" 
              ? { ...stat, userInteractionCount: data.likes?.length || 0 }
              : stat
          )
        }));
      }
    } catch (err) {
      console.error('Error liking blog:', err);
    }
  };

  const handleAddComment = async e => {
    e.preventDefault();
    
    // Authentication check
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!commentText.trim()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/${id}/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ comment: commentText }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Failed to add comment:', errorData.message);
        return;
      }
      
      const data = await res.json();

      // Add new comment to blog
      setBlog(prev => ({
        ...prev,
        comments: [...(prev.comments || []), data.comment],
      }));

      // Update schema data
      if (schemaData) {
        setSchemaData(prev => ({
          ...prev,
          commentCount: prev.commentCount + 1,
          interactionStatistic: prev.interactionStatistic.map(stat => 
            stat.interactionType === "https://schema.org/CommentAction" 
              ? { ...stat, userInteractionCount: prev.commentCount + 1 }
              : stat
          )
        }));
      }
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 animate-fade-in">
        <div className="text-center">
          <div className="animate-pulse rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium tracking-wide">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 animate-fade-in">
        <div className="text-center p-8 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl shadow-2xl">
          <BookOpen className="w-20 h-20 text-gray-400 mx-auto mb-6 animate-pulse" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">Blog not found</h2>
          <button
            onClick={() => navigate('/blog')}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Go back to blogs
          </button>
        </div>
      </div>
    );
  }

  // Calculate read time if not provided
  const wordCount = blog.content ? blog.content.split(/\s+/).length : 0;
  const readTime = blog.readTime || Math.max(1, Math.ceil(wordCount / 200));

  return (
    <>
     {/* Dynamic Breadcrumb Component */}
      <DynamicBreadcrumb additionalData={{ title: blog?.title }} />
      
      {/* JSON-LD Schema Script - ONLY for blog-specific schema */}
      {schemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaData)
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.nevxoaulio.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://www.nevxoaulio.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": blog?.title || "Blog Post",
                "item": window.location.href
              }
            ]
          })
        }}
      />
      {/* SEO Meta Tags */}
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-950 dark:to-gray-900 animate-fade-in">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            {/* Back Button */}
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-8 transition-all duration-300 transform hover:-translate-x-2 font-medium tracking-wide"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blogs</span>
            </button>

            {/* Blog Header */}
            {blog.coverImage && (
              <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl group">
                <img 
                  src={blog.coverImage} 
                  alt={blog.title} 
                  className="w-full h-64 md:h-96 object-cover transform group-hover:scale-105 transition-transform duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            )}

            {/* Category and Breadcrumb */}
            <div className="flex flex-wrap items-center justify-between mb-6 animate-slide-up">
              <span className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-xl font-semibold tracking-wide shadow-lg inline-flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                {blog.category}
              </span>
              
              {/* Visible Breadcrumb for mobile */}
              <nav className="hidden sm:flex items-center text-sm text-gray-500 dark:text-gray-400 mt-4 sm:mt-0">
                <a href="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Home</a>
                <span className="mx-2">›</span>
                <a href="/blog" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Blog</a>
                <span className="mx-2">›</span>
                <span className="text-blue-600 dark:text-blue-400 font-medium truncate max-w-[200px]">{blog.title}</span>
              </nav>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-gray-900 dark:text-white leading-tight tracking-tight animate-fade-up">
              {blog.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm md:text-base text-gray-600 dark:text-gray-400 mb-8 pb-8 border-b border-gray-200/50 dark:border-gray-700/50 animate-slide-up font-medium">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold tracking-wide">{blog.authorName || 'Anonymous Author'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                <time dateTime={blog.publishedAt || blog.createdAt}>
                  {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 md:w-5 md:h-5" />
                <span>{readTime} min read</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 md:w-5 md:h-5" />
                <span>{blog.views || 0} views</span>
              </div>
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-gray-800/50 dark:to-gray-700/50 p-6 rounded-2xl mb-8 border border-blue-100/50 dark:border-blue-900/30 animate-fade-up">
                <p className="text-lg text-gray-700 dark:text-gray-300 italic font-medium leading-relaxed">
                  "{blog.excerpt}"
                </p>
              </div>
            )}

            {/* Blog Content */}
            <article className="prose prose-lg dark:prose-invert max-w-none mb-12 animate-fade-up">
              <div 
                dangerouslySetInnerHTML={{ __html: blog.content }} 
                className="text-gray-700 dark:text-gray-300 leading-relaxed tracking-wide font-medium"
              />
            </article>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mb-8 animate-fade-up">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tags</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <a
                      key={index}
                      href={`/blog?tag=${tag}`}
                      className="px-4 py-2 bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 text-sm rounded-xl font-medium backdrop-blur-sm shadow-sm hover:bg-blue-100/80 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300"
                    >
                      #{tag}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Interaction Bar */}
            <div className="flex items-center justify-between py-6 md:py-8 border-t border-b border-gray-200/50 dark:border-gray-700/50 mb-8 md:mb-12 animate-slide-up">
              <div className="flex items-center gap-4 md:gap-6">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 md:gap-3 transition-all duration-300 transform hover:scale-110 shadow-lg px-4 py-2.5 md:px-5 md:py-3 rounded-xl ${
                    blog.likes?.includes(user?.email)
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-blue-100/80 dark:hover:bg-blue-900/30 backdrop-blur-sm'
                  }`}
                  title={user ? 'Like this post' : 'Login to like'}
                >
                  <ThumbsUp 
                    className="w-5 h-5 md:w-6 md:h-6" 
                    fill={blog.likes?.includes(user?.email) ? 'currentColor' : 'none'} 
                  />
                  <span className="font-semibold tracking-wide">{blog.likes?.length || 0}</span>
                </button>
                <div className="flex items-center gap-2 md:gap-3 text-gray-600 dark:text-gray-400 px-4 py-2.5 md:px-5 md:py-3 bg-gray-100/80 dark:bg-gray-800/80 rounded-xl backdrop-blur-sm shadow-lg">
                  <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
                  <span className="font-semibold tracking-wide">{blog.comments?.length || 0}</span>
                </div>
              </div>
              <button
                onClick={handleShareBlog}
                className="flex items-center gap-2 md:gap-3 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 transform hover:scale-110 px-4 py-2.5 md:px-5 md:py-3 bg-gray-100/80 dark:bg-gray-800/80 rounded-xl backdrop-blur-sm shadow-lg font-medium"
              >
                <Share2 className="w-5 h-5 md:w-6 md:h-6" />
                <span className="font-semibold tracking-wide">{copiedLink ? 'Link Copied!' : 'Share'}</span>
              </button>
            </div>

            {/* Author Bio (Optional) */}
            {blog.authorBio && (
              <div className="mb-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-700/50 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 animate-fade-up">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">About the Author</h3>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {blog.authorName?.charAt(0) || 'A'}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{blog.authorName}</h4>
                    <p className="text-gray-700 dark:text-gray-300">{blog.authorBio}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Related Posts Suggestion (Optional - you can implement this later) */}
            <div className="mb-8 p-6 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl border border-blue-100/50 dark:border-blue-900/30 animate-fade-up">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Enjoyed this article?</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Check out more articles in our <a href="/blog" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">blog</a> 
                or explore our <a href="/services" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">services</a>.
              </p>
              <button
                onClick={() => navigate('/blog')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold"
              >
                View All Articles
              </button>
            </div>

            {/* Comments Section */}
            <div className="mb-8 animate-fade-up">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white tracking-tight">
                Comments ({blog.comments?.length || 0})
              </h2>

              {/* Add Comment Form - Only for logged in users */}
              {user ? (
                <form onSubmit={handleAddComment} className="mb-8 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-gray-200/50 dark:border-gray-700/50">
                  <div className="flex items-start gap-4 md:gap-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
                      {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        placeholder="Write a thoughtful comment..."
                        className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:border-blue-600 focus:outline-none dark:bg-gray-800/50 dark:text-white backdrop-blur-sm resize-none font-medium tracking-wide"
                        rows="4"
                      />
                      <div className="flex justify-end mt-3">
                        <button
                          type="submit"
                          disabled={submitting || !commentText.trim()}
                          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold flex items-center gap-2 tracking-wide"
                        >
                          {submitting ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Posting...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span>Post Comment</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="mb-8 p-8 bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl text-center border border-gray-200/50 dark:border-gray-800/50 backdrop-blur-md shadow-xl">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-blue-600 dark:text-blue-400 animate-pulse" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4 font-medium">
                    Please login to leave a comment
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold tracking-wide"
                  >
                    Login to Comment
                  </button>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-4 md:space-y-6">
                {blog.comments && blog.comments.length > 0 ? (
                  blog.comments.map((comment, index) => (
                    <div
                      key={comment._id || index}
                      className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-gray-200/50 dark:border-gray-800/50 animate-fade-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-start gap-4 md:gap-6">
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
                          {(comment.userName || comment.authorName || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                            <span className="font-bold text-gray-900 dark:text-white text-lg tracking-wide">
                              {comment.userName || comment.authorName || 'Anonymous'}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                              {new Date(comment.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed font-medium tracking-wide">
                            {comment.text || comment.comment || 'No comment text'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 bg-white/80 dark:bg-gray-900/80 rounded-2xl backdrop-blur-md shadow-lg border border-gray-200/50 dark:border-gray-800/50">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-400 animate-pulse" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">
                      No comments yet. Be the first to comment!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Animations */}
          <style jsx>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes fadeUp {
              from { opacity: 0; transform: translateY(30px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            .animate-fade-in {
              animation: fadeIn 0.8s ease-out forwards;
            }
            
            .animate-fade-up {
              animation: fadeUp 0.6s ease-out forwards;
              opacity: 0;
            }
            
            .animate-slide-up {
              animation: slideUp 0.5s ease-out forwards;
              opacity: 0;
            }

            @media (max-width: 768px) {
              .prose {
                font-size: 1rem;
                line-height: 1.75;
              }
            }
          `}</style>
        </div>
    </>
  );
}